import {
  addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, query, serverTimestamp, Timestamp, updateDoc, where
} from "./fs";
import { db } from "../config/firebase";
import { isExcludedIdentity } from "./logPrivacy";

/**
 * Concurrent session registry (`sessions/{id}`), shared with mobile.
 * A session is live while its heartbeat is younger than STALE_MS; the limit
 * counts distinct users per tenant, so extra tabs/devices of the same person
 * do not take another seat.
 */

export const SESSION_LIMIT = Number(process.env.REACT_APP_SESSION_LIMIT) || 20;
export const HEARTBEAT_MS = 60 * 1000;
export const STALE_MS = 3 * 60 * 1000;

const MANAGER_ROLES = ["admin", "company", "dealer", "management"];

let currentId = null;

export function sessionTenant(profile, uid) {
  if (MANAGER_ROLES.includes(profile?.role)) return uid;
  return profile?.parentUid || uid;
}

const millis = (v) => v?.toMillis?.() ?? (v instanceof Date ? v.getTime() : Number(v) || 0);

export function isLive(session, now = Date.now()) {
  return now - millis(session.lastSeen) < STALE_MS;
}

export async function listLiveSessions(tenantId, now = Date.now()) {
  const since = Timestamp.fromDate(new Date(now - STALE_MS));
  const snap = await getDocs(query(collection(db, "sessions"),
  where("tenantId", "==", tenantId), where("lastSeen", ">=", since)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => isLive(s, now));
}

export function countUsers(sessions) {
  return new Set(sessions.map((s) => s.userId)).size;
}

export class SessionLimitError extends Error {
  constructor(limit) {
    super("SESSION_LIMIT");
    this.code = "SESSION_LIMIT";
    this.limit = limit;
  }
}

function deviceLabel() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
}

/** Throws SessionLimitError when the tenant already has SESSION_LIMIT other users online. */
export async function startSession({ uid, profile, platform = "web", appVersion = null, limit = SESSION_LIMIT }) {
  const tenantId = sessionTenant(profile, uid);
  const live = await listLiveSessions(tenantId);
  const others = new Set(live.filter((s) => s.userId !== uid).map((s) => s.userId));
  if (others.size >= limit) throw new SessionLimitError(limit);
  const ref = await addDoc(collection(db, "sessions"), {
    userId: uid,
    tenantId,
    platform,
    appVersion,
    device: isExcludedIdentity({ uid, email: profile?.email }) ? null : deviceLabel(),
    createdAt: serverTimestamp(),
    lastSeen: serverTimestamp()
  });
  currentId = ref.id;
  return ref.id;
}

export function currentSessionId() {
  return currentId;
}

export async function heartbeat(id = currentId) {
  if (!id) return;
  await updateDoc(doc(db, "sessions", id), { lastSeen: serverTimestamp() });
}

/** Call before signOut; afterwards the rules no longer allow the delete. */
export async function endCurrentSession() {
  const id = currentId;
  currentId = null;
  if (id) await deleteDoc(doc(db, "sessions", id)).catch(() => {});
}

export async function revokeSession(id) {
  await deleteDoc(doc(db, "sessions", id));
}

/** Fires `onRevoked` when the session document disappears (revoked by a manager). */
export function watchSession(id, onRevoked) {
  return onSnapshot(doc(db, "sessions", id), (snap) => {
    if (!snap.exists() && currentId === id) {
      currentId = null;
      onRevoked();
    }
  }, () => {});
}

export function __resetSessionsForTests() {
  currentId = null;
}
